import { Icon } from "@cadmakers-ui/icons";
import React, { useEffect, useState } from "react";
import queryString from "query-string";
import styles from "../../../Styles/RelationshipFeedbackDisplay.module.scss";
import { ApiRequest, useShowSnack } from "../../../../Common";
import ResponseHandler from "../../../../App/ResponseHandler";
import { ResponseResult } from "../../../../Common/Models/ResponseResult";
import { endpoints } from "../../../Constants/endpoints";
import { feedbackService } from "../../../Services/FeedbackService";
import {
  Feedback,
  ParentChildFeedback,
  RelationshipDisplayFeedback
} from "../../../Models/Feedback";
import { allContext } from "../../../../Layout/Reducers/SearchContextReducer";
import { RelationshipFeedbackDisplayModal } from "./RelationshipFeedbackDisplayModal";

export type Props = {
  feedback: Feedback;
  parent?: RelationshipDisplayFeedback;
  childrenList?: RelationshipDisplayFeedback[];
  enableEdit: boolean;
  onRelationshipChange?: () => void;
};

export const RelationshipFeedbackDisplayContainer = (props: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const initialOptions: RelationshipDisplayFeedback[] = [];
  const [options, setOptions] = useState(initialOptions);
  const showSnack = useShowSnack();

  // Load parent/child feedback options when the icon is clicked for the dropdown
  // Same app / topic and category
  const onClick = () => {
    let url: string = endpoints.feedback.autocomplete();
    const query = {
      context: allContext.type,
      contextItemId: allContext.itemId ?? null,
      appPrefix: props.feedback.application?.rolePrefix ?? null,
      categoryName: props.feedback.category,
      topicName: props.feedback.topic?.name ?? null,
      currentFeedbackId: props.feedback.id,
      hideClosedFeedbacks: true,
      selectedParentId: props.parent?.value ?? null,
      selectedChildrenIds: props.childrenList?.map(child => child.value)
    };

    const stringified = queryString.stringify(query);
    url += `?${stringified}`;

    ApiRequest.fromPath(url)
      .withAuthHeader()
      .get()
      .then((response: Response) => {
        ResponseHandler.handle(
          response,
          (result: ResponseResult) => {
            setOptions(
              result.payload.map((option: any) => {
                return { value: option.id, label: option.title };
              })
            );
          },
          (result: ResponseResult) => {
            response.json().then(json => {
              showSnack("FEEDBACK_PARENT", {
                label: `Failed to retrieve parent feedbacks: ${json.title} (Error Code ${response.status})`,
                timeout: 5000
              });
            });
          }
        );
      })
      .finally(() => {
        setIsModalOpen(true);
      });
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  // Display links if parent / children exists
  const getRelationshipsDisplay = () => {
    const { parentFeedback, children } = props.feedback;

    if (parentFeedback || (children && children?.length > 0)) {
      return (
        <div className={styles.container}>
          {parentFeedback?.title && (
            <div>
              <div className={styles.label}>Parent</div>
              {parentFeedback?.isActive ? (
                <a
                  className={styles.link}
                  href={feedbackService.getParentChildViewUrl(
                    parentFeedback as ParentChildFeedback
                  )}
                >
                  {parentFeedback?.title}
                </a>
              ) : (
                <span className={styles.disabledlink} title="This feedback has been deleted">
                  {parentFeedback?.title}
                </span>
              )}
            </div>
          )}
          {children && children?.length > 0 && (
            <div>
              <div className={styles.label}>Children</div>
              <ul className={styles.list}>
                {children.map(child => {
                  if (child.isActive) {
                    return (
                      <li>
                        <a
                          className={styles.link}
                          href={feedbackService.getParentChildViewUrl(child as ParentChildFeedback)}
                        >
                          {child.title}
                        </a>
                      </li>
                    );
                  }
                  return (
                    <li>
                      <span className={styles.disabledlink} title="This feedback has been deleted">
                        {child.title}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      );
    }
    return <></>;
  };

  useEffect(() => {
    getRelationshipsDisplay();
  }, [props.feedback]);

  const dropdownContainsOption = (value: number) => {
    for (let i = 0; i < options.length; i++) {
      if (options[i].value === value) {
        return true;
      }
    }
    return false;
  };

  const onRelationshipSelect = (value: number, label: string) => {
    setOptions(options.filter((option: any) => option.value !== value));
  };

  const onRelationshipDeselect = (value: number, label: string) => {
    if (options && !dropdownContainsOption(value)) {
      options.push({ value, label });
      options.sort((a, b) => {
        const labelA = a.label.toLowerCase();
        const labelB = b.label.toLocaleLowerCase();
        if (labelA < labelB) {
          return -1;
        }
        if (labelA > labelB) {
          return 1;
        }
        return 0;
      });
      setOptions(options);
    }
  };

  return (
    <>
      <div className={styles.parentWrapper}>
        {getRelationshipsDisplay()}
        {props.enableEdit && (
          <Icon
            appName="GeneralLayout"
            assetName="POINTER_AddAnchor"
            className={styles.editIcon}
            title="Edit Parent Feedback"
            onClick={onClick}
          />
        )}
      </div>
      <RelationshipFeedbackDisplayModal
        feedback={props.feedback}
        parent={props.parent}
        childrenList={props.childrenList}
        options={options}
        isOpen={isModalOpen}
        toggleModal={toggleModal}
        onRelationshipChange={props.onRelationshipChange}
        onRelationshipSelect={onRelationshipSelect}
        onRelationshipDeselect={onRelationshipDeselect}
      />
    </>
  );
};
